import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../App";
import { platformListSchools, platformCreateSchool, platformSuggestPassword, platformSetSchoolActive, platformGetPrivacyPolicy, platformPublishPrivacyPolicy, platformSetSchoolGoalImport } from "../lib/api";
import { toast } from "sonner";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const emptyForm = { name: "", admin_name: "", admin_email: "", temp_password: "" };

export default function PlatformAdminPage() {
  const { user, logout } = useAuth();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // {admin_email, temp_password, name}

  // Privacy policy
  const [policyContent, setPolicyContent] = useState("");
  const [policyVersion, setPolicyVersion] = useState(0);
  const [policyPublishedAt, setPolicyPublishedAt] = useState(null);
  const [policyBusy, setPolicyBusy] = useState(false);

  const loadPolicy = useCallback(async () => {
    try {
      const res = await platformGetPrivacyPolicy();
      setPolicyContent(res.data.content || "");
      setPolicyVersion(res.data.version || 0);
      setPolicyPublishedAt(res.data.published_at || null);
    } catch { /* optional */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await platformListSchools();
      setSchools(res.data.schools || []);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not load schools");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadPolicy(); }, [load, loadPolicy]);

  const openCreate = async () => {
    setForm(emptyForm);
    setCreated(null);
    setShowCreate(true);
    try {
      const res = await platformSuggestPassword();
      setForm((f) => ({ ...f, temp_password: res.data.temp_password }));
    } catch { /* optional */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.admin_name.trim() || !form.admin_email.trim()) {
      return toast.error("School name, admin name and admin email are required");
    }
    setBusy(true);
    try {
      const res = await platformCreateSchool(form);
      setCreated({ name: form.name, admin_email: res.data.admin_email, temp_password: res.data.temp_password });
      toast.success("School created");
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not create school");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (school) => {
    const suspending = school.active !== false;
    if (suspending && !window.confirm(`Suspend "${school.name}"? Its users will be unable to log in until you reactivate it. Data is preserved.`)) return;
    try {
      await platformSetSchoolActive(school.id, !suspending);
      toast.success(suspending ? "School suspended" : "School reactivated");
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not update school");
    }
  };

  const toggleGoalImport = async (school) => {
    const enabling = !school.goal_import_enabled;
    try {
      await platformSetSchoolGoalImport(school.id, enabling);
      toast.success(enabling ? "Goal import enabled" : "Goal import disabled");
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not update school");
    }
  };

  const handlePublishPolicy = async () => {
    if (!policyContent.trim()) return toast.error("Policy content is required");
    if (!window.confirm("Publish this privacy policy? Every user will be required to accept it before continuing to use the app.")) return;
    setPolicyBusy(true);
    try {
      const res = await platformPublishPrivacyPolicy(policyContent);
      setPolicyVersion(res.data.version);
      toast.success(`Privacy policy published (v${res.data.version}). All users must now re-accept.`);
      await loadPolicy();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not publish policy");
    } finally {
      setPolicyBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#0b1e3b" }}>
      <header className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src="/logo_full.png" alt="SMARTIS" className="h-9 w-auto rounded" />
          <div>
            <h1 className="text-lg font-bold text-white tracking-tight">SMARTIS · Platform Admin</h1>
            <p className="text-xs text-white/50">{user?.email}</p>
          </div>
        </div>
        <button onClick={logout} data-testid="platform-logout" className="rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20">
          Sign Out
        </button>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Schools</h2>
            <p className="text-sm text-white/60">Each school has fully isolated data. Create a school and assign its first administrator.</p>
          </div>
          <button onClick={openCreate} data-testid="platform-create-school-btn" className="rounded-lg bg-[#C84E2A] px-4 py-2.5 font-semibold text-white hover:opacity-90">
            + Create School
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-white" />
          </div>
        ) : schools.length === 0 ? (
          <div className="rounded-xl bg-white/5 p-10 text-center text-white/60" data-testid="platform-empty">
            No schools yet. Create your first one.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="platform-schools-grid">
            {schools.map((s) => {
              const suspended = s.active === false;
              return (
              <div key={s.id} data-testid={`school-card-${s.id}`} className={`rounded-xl bg-white p-5 shadow-lg ${suspended ? "opacity-70 ring-2 ring-amber-300" : ""}`}>
                <div className="flex items-start justify-between">
                  <h3 className="text-lg font-bold text-[#00305E]">{s.name}</h3>
                  {s.is_default ? (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-500">Default</span>
                  ) : suspended ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-700">Suspended</span>
                  ) : (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">Active</span>
                  )}
                </div>
                {s.admin_email && <p className="mt-1 text-xs text-slate-500">Admin: {s.admin_email}</p>}
                {s.goal_import_enabled && (
                  <span className="mt-2 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-indigo-700" data-testid={`goal-import-flag-${s.id}`}>
                    Goal Import ON
                  </span>
                )}
                <div className="mt-4 flex gap-6">
                  <div>
                    <p className="text-2xl font-bold text-[#00305E]">{s.student_count ?? 0}</p>
                    <p className="text-xs text-slate-500">Students</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#00305E]">{s.staff_count ?? 0}</p>
                    <p className="text-xs text-slate-500">Staff</p>
                  </div>
                </div>
                {!s.is_default && (
                  <button
                    onClick={() => toggleActive(s)}
                    data-testid={`school-toggle-active-${s.id}`}
                    className={`mt-4 w-full rounded-lg py-2 text-sm font-semibold transition-colors ${
                      suspended
                        ? "bg-emerald-600 text-white hover:bg-emerald-700"
                        : "border border-amber-400 text-amber-700 hover:bg-amber-50"
                    }`}
                  >
                    {suspended ? "Reactivate" : "Suspend"}
                  </button>
                )}
                <button
                  onClick={() => toggleGoalImport(s)}
                  data-testid={`school-toggle-goal-import-${s.id}`}
                  className={`mt-2 w-full rounded-lg py-2 text-sm font-semibold transition-colors ${
                    s.goal_import_enabled
                      ? "border border-indigo-400 text-indigo-700 hover:bg-indigo-50"
                      : "bg-indigo-600 text-white hover:bg-indigo-700"
                  }`}
                >
                  {s.goal_import_enabled ? "Disable Goal Import" : "Enable Goal Import"}
                </button>
              </div>
              );
            })}
          </div>
        )}

        {/* Privacy Policy management */}
        <div className="mt-12 border-t border-white/10 pt-8" data-testid="platform-privacy-section">
          <h2 className="text-2xl font-bold text-white">Privacy Policy</h2>
          <p className="text-sm text-white/60">
            Publish a new privacy policy. Bumping the version forces <strong>every user</strong> to accept it before they can continue using the app.
          </p>
          <div className="mt-4 rounded-xl bg-white p-5 shadow-lg">
            <div className="mb-3 flex items-center justify-between">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600" data-testid="privacy-current-version">
                {policyVersion > 0 ? `Current version: v${policyVersion}` : "No policy published yet"}
              </span>
              {policyPublishedAt && (
                <span className="text-xs text-slate-400">Published {new Date(policyPublishedAt).toLocaleString()}</span>
              )}
            </div>
            <textarea
              value={policyContent}
              onChange={(e) => setPolicyContent(e.target.value)}
              rows={12}
              placeholder="Paste the full privacy policy text here…"
              data-testid="privacy-policy-textarea"
              className="w-full rounded-lg border border-slate-300 p-3 text-sm focus:border-[#00305E] focus:outline-none"
            />
            <div className="mt-3 flex justify-end">
              <button
                onClick={handlePublishPolicy}
                disabled={policyBusy}
                data-testid="privacy-publish-btn"
                className="rounded-lg bg-[#C84E2A] px-5 py-2.5 font-semibold text-white hover:opacity-90 disabled:opacity-60"
              >
                {policyBusy ? "Publishing…" : "Publish New Version & Require Acceptance"}
              </button>
            </div>
          </div>
        </div>
      </main>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={() => !busy && setShowCreate(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="create-school-modal">
            {!created ? (
              <>
                <h3 className="mb-4 text-xl font-bold text-[#00305E]">Create a new school</h3>
                <form onSubmit={submit} className="space-y-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">School name</label>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                      data-testid="cs-name" className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-[#00305E] focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">First admin — full name</label>
                    <input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} required
                      data-testid="cs-admin-name" className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-[#00305E] focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">First admin — email</label>
                    <input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} required
                      data-testid="cs-admin-email" className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-[#00305E] focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Temporary password</label>
                    <input value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} required
                      data-testid="cs-temp-password" className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono focus:border-[#00305E] focus:outline-none" />
                    <p className="mt-1 text-xs text-slate-400">The admin will be required to change this on first login.</p>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button type="button" onClick={() => setShowCreate(false)} disabled={busy}
                      className="flex-1 rounded-lg border border-slate-300 py-2.5 font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                    <button type="submit" disabled={busy} data-testid="cs-submit"
                      className="flex-1 rounded-lg bg-[#00305E] py-2.5 font-semibold text-white hover:opacity-90 disabled:opacity-60">
                      {busy ? "Creating…" : "Create school"}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div data-testid="create-school-success">
                <h3 className="mb-2 text-xl font-bold text-[#00305E]">School created</h3>
                <p className="text-sm text-slate-600">Share these credentials with <strong>{created.name}</strong>'s administrator. They'll set a new password on first login.</p>
                <div className="mt-4 space-y-2 rounded-lg bg-slate-50 p-4 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="font-mono font-semibold">{created.admin_email}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Temp password</span><span className="font-mono font-semibold">{created.temp_password}</span></div>
                </div>
                <button onClick={() => setShowCreate(false)} data-testid="cs-done"
                  className="mt-5 w-full rounded-lg bg-[#00305E] py-2.5 font-semibold text-white hover:opacity-90">Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
